import BottomBarPadding from "@components/BottomBarPadding";
import LBox, { BackgroundColorProp } from "@components/LBox";
import { LGradientProps } from "@components/LGradient";
import TopBarPadding from "@components/TopBarPadding";
import { BoxProps } from "@shopify/restyle";
import { Theme, theme } from "@theme";
import { KeyboardAvoidingView } from "@components/KeyboardAvoidingView";
import { useSafeAreaInsets } from "react-native-safe-area-context";

interface ScreenProps
  extends Omit<BoxProps<Theme>, "backgroundColor">,
    LGradientProps,
    BackgroundColorProp {
  children?: React.ReactNode;
  enableAutomaticBottomPadding?: boolean;
  enableAutomaticTopPadding?: boolean;
  keyboard?: boolean;
  keyboardVerticalOffset?: number;
  safeArea?: boolean;
  safeAreaEdges?: ("top" | "right" | "bottom" | "left")[];
  transparentHeader?: boolean;
}

const Container = ({
  keyboard,
  keyboardVerticalOffset,
  children,
  backgroundColor,
}: ScreenProps) => {
  if (keyboard) {
    return (
      <KeyboardAvoidingView
        style={{
          flex: 1,
          backgroundColor: backgroundColor
            ? (backgroundColor as string)
            : theme.colors.screenBg,
        }}
        behavior="padding"
        keyboardVerticalOffset={keyboardVerticalOffset}
      >
        <LBox flex={1}>{children}</LBox>
      </KeyboardAvoidingView>
    );
  }

  return <>{children}</>;
};

const SafeArea = ({
  children,
  safeArea = false,
  safeAreaEdges = ["top"],
}: ScreenProps) => {
  const insets = useSafeAreaInsets();

  const safeAreaTop =
    safeArea && safeAreaEdges?.includes("top") ? insets.top : 0;
  const safeAreaBottom =
    safeArea && safeAreaEdges?.includes("bottom") ? insets.bottom : 0;
  const safeAreaLeft =
    safeArea && safeAreaEdges?.includes("left") ? insets.left : 0;
  const safeAreaRight =
    safeArea && safeAreaEdges?.includes("right") ? insets.right : 0;
  if (safeArea) {
    return (
      <LBox
        style={{
          flex: 1,
          paddingTop: safeAreaTop,
          paddingBottom: safeAreaBottom,
          paddingLeft: safeAreaLeft,
          paddingRight: safeAreaRight,
        }}
      >
        {children}
      </LBox>
    );
  }

  return <>{children}</>;
};

const LScreen = (props: ScreenProps) => {
  const {
    children,
    enableAutomaticBottomPadding = false,
    enableAutomaticTopPadding = false,
    transparentHeader = false,
    ...rest
  } = props;
  return (
    <Container {...props}>
      <LBox
        flex={1}
        paddingHorizontal="s4"
        backgroundColor="screenBg"
        paddingTop={transparentHeader ? "l3" : undefined}
        {...rest}
      >
        <SafeArea {...props}>
          {enableAutomaticTopPadding && <TopBarPadding />}
          {children}
        </SafeArea>
        {enableAutomaticBottomPadding && <BottomBarPadding />}
      </LBox>
    </Container>
  );
};

export default LScreen;
